import { useEffect, useState, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { ProductCard } from "../components/ProductCard";
import { Footer } from "../components/Footer";
import { EmptyStateCTA } from "../components/EmptyStateCTA";
import { SUPPORT_WHATSAPP_LINK } from "../lib/constants";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Search, SlidersHorizontal, BadgeCheck } from "lucide-react";

export default function Marketplace() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: "all", condition: "all", search: "", size: "", location: "", min_price: "", max_price: "", wiltom_only: false, sort: "newest" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category !== "all") params.category = filters.category;
      if (filters.condition !== "all") params.condition = filters.condition;
      if (filters.search) params.search = filters.search;
      if (filters.size) params.size = filters.size;
      if (filters.location) params.location = filters.location;
      if (filters.min_price) params.min_price = filters.min_price;
      if (filters.max_price) params.max_price = filters.max_price;
      if (filters.wiltom_only) params.wiltom_only = true;
      if (filters.sort === "wiltom_first") params.sort = "wiltom_first";
      const { data } = await api.get("/products", { params });
      setProducts(data);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="border-b border-foreground bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
          <span className="label-caps text-accent">Marketplace</span>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Industrial tyres & parts</h1>
          <p className="text-white/60 mt-2 text-sm">Source from verified suppliers. Send an inquiry in seconds.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Filters */}
        <aside className="lg:col-span-1">
          <div className="border border-border p-5 sticky top-20">
            <div className="flex items-center gap-2 mb-4">
              <SlidersHorizontal className="h-4 w-4 text-primary" />
              <span className="label-caps">Filters</span>
            </div>
            <div className="space-y-4">
              <div className="border border-primary/40 bg-primary/5 p-3" data-testid="wiltom-filter-box">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <Checkbox checked={filters.wiltom_only} onCheckedChange={(v) => setFilters({ ...filters, wiltom_only: !!v })} data-testid="filter-wiltom-only" />
                  <BadgeCheck className="h-4 w-4 text-primary" /> Official WILTOM only
                </label>
                <p className="text-xs text-muted-foreground mt-1">Show only WILTOM Verified listings.</p>
              </div>
              <div>
                <label className="label-caps text-muted-foreground">Sort by</label>
                <Select value={filters.sort} onValueChange={(v) => setFilters({ ...filters, sort: v })}>
                  <SelectTrigger className="rounded-none mt-2 h-10" data-testid="filter-sort"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest first</SelectItem>
                    <SelectItem value="wiltom_first">Official WILTOM first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps text-muted-foreground">Search</label>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                         className="rounded-none pl-9 h-10" placeholder="Brand, title…" data-testid="filter-search" />
                </div>
              </div>
              <div>
                <label className="label-caps text-muted-foreground">Category</label>
                <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                  <SelectTrigger className="rounded-none mt-2 h-10" data-testid="filter-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((c) => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps text-muted-foreground">Stock type / Condition</label>
                <Select value={filters.condition} onValueChange={(v) => setFilters({ ...filters, condition: v })}>
                  <SelectTrigger className="rounded-none mt-2 h-10" data-testid="filter-condition"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any condition</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="used">Used</SelectItem>
                    <SelectItem value="refurbished">Refurbished</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps text-muted-foreground">Tyre size</label>
                <Input value={filters.size} onChange={(e) => setFilters({ ...filters, size: e.target.value })}
                       className="rounded-none mt-2 h-10" placeholder="e.g. 295/80 R22.5" data-testid="filter-size" />
              </div>
              <div>
                <label className="label-caps text-muted-foreground">Location</label>
                <Input value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                       className="rounded-none mt-2 h-10" placeholder="e.g. Berlin" data-testid="filter-location" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label-caps text-muted-foreground">Min £</label>
                  <Input type="number" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                         className="rounded-none mt-2 h-10" data-testid="filter-min" />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground">Max £</label>
                  <Input type="number" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                         className="rounded-none mt-2 h-10" data-testid="filter-max" />
                </div>
              </div>
              <Button variant="outline" className="w-full rounded-none border-foreground" data-testid="filter-clear"
                      onClick={() => setFilters({ category: "all", condition: "all", search: "", size: "", location: "", min_price: "", max_price: "", wiltom_only: false, sort: "newest" })}>
                Clear filters
              </Button>
            </div>
          </div>
        </aside>

        {/* Grid */}
        <main className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-muted-foreground" data-testid="product-count">{products.length} products</span>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/3] bg-muted animate-pulse border border-border" />)}
            </div>
          ) : products.length === 0 && filters.wiltom_only ? (
            <EmptyStateCTA
              testid="empty-wiltom-products"
              icon={BadgeCheck}
              title="No Official WILTOM listings are available yet. Please check back soon or contact WILTOM on WhatsApp."
              subtitle="Official WILTOM listings are sourced and verified directly by the WILTOM team."
              actions={[
                { label: "Contact WILTOM on WhatsApp", href: SUPPORT_WHATSAPP_LINK, variant: "whatsapp", testid: "wiltom-empty-whatsapp" },
                { label: "Browse all listings", to: "#", variant: "primary", testid: "wiltom-empty-browse-all", onClick: () => setFilters({ ...filters, wiltom_only: false }) },
              ]}
            />
          ) : products.length === 0 ? (
            <EmptyStateCTA
              testid="empty-products"
              title="New verified listings are coming soon. Be the first supplier to list your products on WILTOM TradeLink."
              subtitle="List tyres, truck parts, commercial stock and export goods for verified B2B buyers across Europe."
              actions={[
                { label: "Register as Supplier", to: "/register", variant: "primary", testid: "empty-register-supplier" },
                { label: "Post a Product", to: "/seller", testid: "empty-post-product" },
                { label: "Contact WILTOM on WhatsApp", href: SUPPORT_WHATSAPP_LINK, variant: "whatsapp", testid: "empty-whatsapp" },
              ]}
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
}
